import { create } from "zustand";
import { scopedIdbGet as idbGet, scopedIdbSet as idbSet } from "@/lib/client-scoped-idb";
import { deleteBlob, loadBlob, saveBlob } from "@/lib/blob-store";
import { MIME_EXT, extFromFilename, mimeFromExt } from "@/lib/mime-ext";
import { DocsApiError, isDocsAuthReady } from "@/lib/docs-api";
import { GithubStorageError } from "@/lib/github-storage";
import {
  deleteWorkshopBlob,
  downloadWorkshopBlob,
  isWorkshopBlobSyncEnabled,
  listWorkshopBlobs,
  uploadWorkshopBlob,
} from "@/lib/workshop-blob-sync";
import {
  deleteSearchIndex,
  isExtractableExt,
  isExtractableMime,
  uploadIndexMarkdown,
  upsertSearchIndex,
} from "@/lib/search-index-sidecar";
import { broadcastSync } from "@/lib/sync-broadcast";
import { isWorkshopBffEnabled } from "@/env";
import type { Workshop } from "@/content/workshops";

/**
 * AssetLocker store — per-workshop list of facilitator-curated
 * deliverable chips. Mirror of `useFindings` (same shape, same API);
 * the two stores are intentionally kept in lockstep so the
 * `Findings` and `AssetLocker` panels can share the chip UI.
 *
 * Backed by `idb-keyval` under `xaes-asset-locker`. Payloads are
 * pointed at via `blobKey` (see `src/lib/blob-store.ts`); data-URL
 * uploads previously stored on `Upload.url` have been removed —
 * chat exports now route through `useCaptures` instead.
 *
 * Phase 6 — when a blob backend is enabled the backend is the source of
 * truth; IDB is the local cache (fast hydrate + offline). The backend is
 * chosen in `workshop-blob-sync.ts`: the production BFF / Azure backend, or
 * the legacy GitHub fallback. The `sync()` action does a pull-then-push
 * reconciliation per workshop; `add()` / `remove()` mirror writes through the
 * adapter fire-and-forget so the local UI stays responsive even when the
 * network is slow. When no backend is configured we fall back to the original
 * IDB-only behaviour — no regression for the offline dev path. See
 * `docs/adr/0010-github-persistence.md` for the legacy GitHub rationale.
 *
 * Adapted in spirit from `Loveable v1/src/components/AssetLocker.tsx`,
 * but lifted to Zustand + idb-keyval per the Phase 5a Slice 2
 * improvement axis (see `plans/REFACTOR_PLAN.md`).
 */

export type UploadAccent = "blue" | "green" | "gold" | "melon" | "peach";

export type Upload = {
  /** uuid-ish, unique within the workshop scope */
  id: string;
  label: string;
  /** lucide icon name (see `AssetLocker.tsx` ICON_CHOICES) or "" for default */
  icon: string;
  color: UploadAccent;
  /**
   * Pointer into `src/lib/blob-store.ts`. Present for uploads that
   * carry binary payloads (snapshot PNGs, file exports, etc.). The
   * blob is fetched on click — never kept in zustand memory.
   */
  blobKey?: string;
  /** MIME type of `blobKey` payload — e.g. "image/png", "text/markdown". */
  mimeType?: string;
  /**
   * Optional explicit file-format shortcode (e.g. "PDF", "DOCX",
   * "XLSX", "ZIP"). When set, `AssetLocker`'s `formatTag()`
   * renders it instead of deriving from `mimeType`. Useful for seed
   * entries that have no real payload yet but still want a
   * legacy-style format chip.
   */
  format?: string;
  /**
   * Optional published URL for the asset (e.g. a CDN / blob URL once the
   * upload has been published out of the workshop). Undefined until then;
   * the analyst reference resolver renders "(not yet published)" when absent.
   */
  url?: string;
  addedAt: number;
  addedBy: string;
  /** Free-form tags (P0.1 reserved seam). Optional; absent on legacy
   *  entries, defaulted to `[]` at every read site. */
  tags?: string[];
  /**
   * Phase 6 — GitHub Contents API path, e.g.
   * `"workshops/WS4/uploads/abc.png"`. Set once the upload has synced.
   * Used as the delete key + the durability signal (absent → IDB-only).
   */
  githubPath?: string;
  /**
   * Phase 6 — GitHub blob SHA. Required to delete or update an asset
   * (the Contents API uses it as an optimistic-concurrency token).
   * Refreshed on each successful upload.
   */
  githubSha?: string;
  /**
   * Phase 6 — ISO timestamp from the last GitHub commit touching this
   * asset. Drives the "last synced" UI hint.
   */
  githubSyncedAt?: string;
};

type EntriesByWorkshop = Record<Workshop["id"], Upload[]>;

/**
 * Phase 6 — adapter-mirror sync state surfaced on the store so the
 * workshop header can render a status pill ("synced" / "syncing" / …).
 *
 *   - `disabled` → no GitHub creds; IDB-only mode
 *   - `idle`     → sync enabled, nothing in flight
 *   - `syncing`  → reconciliation in progress
 *   - `synced`   → last reconciliation finished cleanly
 *   - `error`    → last attempt failed; `syncError` carries the message
 */
type SyncState = "idle" | "syncing" | "synced" | "error" | "disabled";

type AssetLockerState = {
  entriesByWorkshop: EntriesByWorkshop;
  isHydrated: boolean;
  /** Adapter status — see {@link SyncState}. */
  syncState: SyncState;
  /** ms epoch of the last successful `sync()`; `null` until the first. */
  lastSyncedAt: number | null;
  /** Human-readable error from the most recent failure; cleared on success. */
  syncError: string | null;
  hydrate: () => Promise<void>;
  add: (workshopId: Workshop["id"], upload: Upload) => void;
  /**
   * P0.1 — patch-merge an entry's editable presentation fields by id.
   * IDB-only by design: tag/label edits must NOT round-trip the
   * GitHub-sync path (no re-upload, no SHA churn). The blob + its mirror
   * metadata are untouched.
   */
  update: (
    workshopId: Workshop["id"],
    id: string,
    patch: UploadPatch,
  ) => void;
  remove: (workshopId: Workshop["id"], id: string) => void;
  /**
   * Pull-then-push reconciliation against GitHub for every workshop
   * bucket. No-op when sync is disabled. See implementation comment
   * for the per-step semantics.
   */
  sync: () => Promise<void>;
};

/** Editable presentation fields for {@link AssetLockerState.update}.
 *  Excludes the blob pointer + GitHub mirror triplet (IDB-only edits). */
export type UploadPatch = Partial<
  Pick<Upload, "label" | "icon" | "color" | "tags">
>;

const IDB_KEY = "xaes-asset-locker";

const WORKSHOP_IDS: Workshop["id"][] = [
  "WS1",
  "WS2",
  "WS3",
  "WS4",
  "WS5",
  "WS6",
  "WS7",
  "WS8",
];

const emptyEntries = (): EntriesByWorkshop => ({
  WS1: [],
  WS2: [],
  WS3: [],
  WS4: [],
  WS5: [],
  WS6: [],
  WS7: [],
  WS8: [],
});

/**
 * Starter assets seeded into each workshop on first load so the
 * AssetLocker reads as "populated" out of the box. Mirrors the
 * legacy `XAES_CONTENT.<ws>.assets` lists that fed the locker chip
 * grid in `_legacy/xaes-content.js`. Users can still remove or rename
 * any chip — once they do, the new state persists in IDB and these
 * seeds no longer re-apply for that workshop.
 *
 * Icons match `AssetLocker.ICON_CHOICES`: FileText, Image, Headphones,
 * Database, ClipboardCheck, Users, Calendar, MapPin, PlayCircle.
 */
const SEED_ASSETS: EntriesByWorkshop = {
  WS1: [
    { id: "ws1-charter", label: "Executive charter", icon: "FileText", format: "PDF", color: "melon", addedAt: 0, addedBy: "seed" },
    { id: "ws1-stakeholders", label: "Stakeholder map", icon: "Users", format: "DOCX", color: "melon", addedAt: 0, addedBy: "seed" },
    { id: "ws1-outcomes", label: "Outcomes statement", icon: "ClipboardCheck", format: "DOCX", color: "melon", addedAt: 0, addedBy: "seed" },
  ],
  WS2: [
    { id: "ws2-current-map", label: "As-is process map", icon: "Database", format: "PDF", color: "melon", addedAt: 0, addedBy: "seed" },
    { id: "ws2-pain-points", label: "Pain-point log", icon: "FileText", format: "DOCX", color: "melon", addedAt: 0, addedBy: "seed" },
    { id: "ws2-baseline", label: "Baseline metrics", icon: "Database", format: "XLSX", color: "melon", addedAt: 0, addedBy: "seed" },
  ],
  WS3: [
    // WS3 deliverables — regenerated set. Themes around process
    // mapping + future-state design (the workshop's stated focus).
    { id: "ws3-process-map", label: "Process map", icon: "Database", format: "PDF", color: "melon", addedAt: 0, addedBy: "seed" },
    { id: "ws3-pain-points", label: "Pain-point summary", icon: "FileText", format: "DOCX", color: "melon", addedAt: 0, addedBy: "seed" },
    { id: "ws3-quick-wins", label: "Quick-wins backlog", icon: "ClipboardCheck", format: "XLSX", color: "melon", addedAt: 0, addedBy: "seed" },
    { id: "ws3-future-state", label: "Future-state vision", icon: "Image", format: "PDF", color: "melon", addedAt: 0, addedBy: "seed" },
    { id: "ws3-redesign-brief", label: "Workflow redesign brief", icon: "Pencil", format: "DOCX", color: "melon", addedAt: 0, addedBy: "seed" },
    { id: "ws3-handoff-model", label: "Hand-off model", icon: "Users", format: "PDF", color: "melon", addedAt: 0, addedBy: "seed" },
  ],
  WS4: [
    // WS4 deliverables — the legacy 6-card hardcoded Deliverables list
    // (Brief / Solution Design / Team Members / Telemetry / Downloads /
    // Stakeholder) ported into the store so user uploads (chat exports,
    // HAH/STR snapshots) sit alongside them in the same grid. Format
    // tags track the REAL seeded file formats (see infra/
    // seed-deliverables.mjs SEED — doc→docx, xls→xlsx, cad→pdf).
    { id: "ws4-brief", label: "Brief", icon: "FileText", format: "PDF", color: "melon", addedAt: 0, addedBy: "seed" },
    { id: "ws4-solution", label: "Solution Design", icon: "Pencil", format: "PDF", color: "melon", addedAt: 0, addedBy: "seed" },
    { id: "ws4-team", label: "Team Members", icon: "Users", format: "DOCX", color: "melon", addedAt: 0, addedBy: "seed" },
    { id: "ws4-telemetry", label: "Telemetry Data", icon: "ChartBar", format: "XLSX", color: "melon", addedAt: 0, addedBy: "seed" },
    { id: "ws4-downloads", label: "Download Files", icon: "HardDrive", format: "ZIP", color: "melon", addedAt: 0, addedBy: "seed" },
    { id: "ws4-stakeholder", label: "Stakeholder Assessment", icon: "FileText", format: "DOCX", color: "melon", addedAt: 0, addedBy: "seed" },
  ],
  WS5: [
    { id: "ws5-controls", label: "Controls & guardrails", icon: "FileText", format: "PDF", color: "melon", addedAt: 0, addedBy: "seed" },
    { id: "ws5-policies", label: "Policy set", icon: "ClipboardCheck", format: "DOCX", color: "melon", addedAt: 0, addedBy: "seed" },
    { id: "ws5-audit", label: "Audit & traceability plan", icon: "Database", format: "XLSX", color: "melon", addedAt: 0, addedBy: "seed" },
  ],
  WS6: [
    { id: "ws6-blueprint", label: "Phase 0 blueprint", icon: "FileText", format: "PDF", color: "melon", addedAt: 0, addedBy: "seed" },
    { id: "ws6-tom", label: "Target operating model", icon: "Database", format: "PDF", color: "melon", addedAt: 0, addedBy: "seed" },
    { id: "ws6-demo", label: "End-to-end demo deck", icon: "PlayCircle", format: "PDF", color: "melon", addedAt: 0, addedBy: "seed" },
  ],
  WS7: [
    { id: "ws7-deck", label: "Executive showcase deck", icon: "PlayCircle", format: "PDF", color: "melon", addedAt: 0, addedBy: "seed" },
    { id: "ws7-evidence", label: "Outcome evidence pack", icon: "FileText", format: "ZIP", color: "melon", addedAt: 0, addedBy: "seed" },
    { id: "ws7-decisions", label: "Decision log", icon: "ClipboardCheck", format: "DOCX", color: "melon", addedAt: 0, addedBy: "seed" },
  ],
  WS8: [
    { id: "ws8-flight-plan", label: "Flight plan", icon: "Calendar", format: "PDF", color: "melon", addedAt: 0, addedBy: "seed" },
    { id: "ws8-milestones", label: "Phase 0 → 2+ milestones", icon: "MapPin", format: "XLSX", color: "melon", addedAt: 0, addedBy: "seed" },
    { id: "ws8-rituals", label: "Adoption rituals", icon: "Users", format: "DOCX", color: "melon", addedAt: 0, addedBy: "seed" },
  ],
};

/**
 * Marker key — once seeding has run for a given seed version, set this
 * in IDB so we don't re-pour starter assets on top of a user's edits
 * next reload. The value stored is the version string; bump
 * `SEED_VERSION` whenever `SEED_ASSETS` changes shape (e.g. the WS3
 * regeneration + WS4 Deliverables port did so) and the hydrate path
 * will re-seed all `addedBy: "seed"` entries while leaving
 * user-added uploads untouched.
 */
const SEED_MARKER_KEY = "xaes-asset-locker-seeded";
const SEED_VERSION = "v6-real-doc-formats";

/**
 * Best-effort sanitiser. See `useFindings.ts` — same threat model.
 *
 * Phase 6 — preserves the GitHub mirror fields (`githubPath`,
 * `githubSha`, `githubSyncedAt`) so a reload doesn't orphan the remote
 * asset (we'd otherwise have to re-upload + leak a duplicate).
 */
function sanitise(raw: unknown): EntriesByWorkshop {
  const out = emptyEntries();
  if (typeof raw !== "object" || raw === null) return out;
  const obj = raw as Record<string, unknown>;
  for (const id of Object.keys(out) as Array<Workshop["id"]>) {
    const list = obj[id];
    if (!Array.isArray(list)) continue;
    const cleaned: Upload[] = [];
    for (const item of list) {
      if (typeof item !== "object" || item === null) continue;
      const u = item as Record<string, unknown>;
      if (typeof u.id !== "string") continue;
      if (typeof u.label !== "string") continue;
      const icon = typeof u.icon === "string" ? u.icon : "";
      const color: UploadAccent =
        u.color === "blue" ||
        u.color === "green" ||
        u.color === "gold" ||
        u.color === "melon" ||
        u.color === "peach"
          ? u.color
          : "gold";
      cleaned.push({
        id: u.id,
        label: u.label,
        icon,
        color,
        // `blobKey` + `mimeType` carry binary uploads — preserved
        // verbatim so a reload doesn't orphan the blob-store entry.
        blobKey: typeof u.blobKey === "string" ? u.blobKey : undefined,
        mimeType: typeof u.mimeType === "string" ? u.mimeType : undefined,
        format: typeof u.format === "string" ? u.format : undefined,
        addedAt: typeof u.addedAt === "number" ? u.addedAt : Date.now(),
        addedBy: typeof u.addedBy === "string" ? u.addedBy : "",
        // P0.1 — free-form tags; preserved when present, absent on legacy
        // entries. Coerced + filtered to strings.
        tags: Array.isArray(u.tags)
          ? u.tags.filter((t): t is string => typeof t === "string")
          : undefined,
        // Phase 6 — GitHub mirror metadata. Strings only; absent on
        // entries that haven't synced yet (IDB-only mode or pre-sync).
        githubPath: typeof u.githubPath === "string" ? u.githubPath : undefined,
        githubSha: typeof u.githubSha === "string" ? u.githubSha : undefined,
        githubSyncedAt:
          typeof u.githubSyncedAt === "string" ? u.githubSyncedAt : undefined,
      });
    }
    out[id] = cleaned;
  }
  return out;
}

async function persist(entries: EntriesByWorkshop): Promise<void> {
  try {
    await idbSet(IDB_KEY, entries);
  } catch {
    /* idb-keyval write failed — best-effort durability, see jsdoc */
  }
}

/**
 * Derive the mirrored blob filename for an upload. The name leads with
 * the human label (what the user dropped) plus a short id suffix for
 * uniqueness — `Stakeholder Assessment-3f9a02c1.pdf` — because the
 * ingestion worker routes on the blob path's extension and the XAES
 * Analyst cites the blob filename: a bare `<uuid>` name made every
 * deliverable unindexable (no extension) or unreadable (uuid citation).
 * The extension prefers the {@link MIME_EXT} map and falls back to the
 * label's own extension; when neither knows, we still upload (the bytes
 * are the point) and `mimeFromExt` defaults the round-trip back to
 * `application/octet-stream`.
 */
function uploadFilename(upload: Upload): string {
  const ext =
    (upload.mimeType && MIME_EXT[upload.mimeType]) ||
    extFromFilename(upload.label);
  const stem =
    upload.label
      .replace(/\.[^.]+$/, "")
      .replace(/[^\w\- ]+/g, "")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 64) || upload.id;
  const named = stem === upload.id ? stem : `${stem}-${upload.id.slice(0, 8)}`;
  return ext ? `${named}.${ext}` : named;
}

function uploadIsExtractable(upload: Upload): boolean {
  if (isExtractableMime(upload.mimeType)) return true;
  const ext =
    (upload.mimeType && MIME_EXT[upload.mimeType]) ||
    (upload.format ? upload.format.toLowerCase() : "") ||
    extFromFilename(upload.label) ||
    extFromFilename(uploadFilename(upload));
  return Boolean(ext && isExtractableExt(ext));
}

function upsertUploadIndex(workshopId: Workshop["id"], upload: Upload): void {
  if (!isWorkshopBffEnabled()) return;
  void upsertSearchIndex(
    workshopId,
    upload.id,
    uploadIndexMarkdown({
      workshopId,
      id: upload.id,
      label: upload.label,
      mimeType: upload.mimeType,
      format: upload.format,
      tags: upload.tags,
      extractable: uploadIsExtractable(upload),
    }),
  );
}

/** Best-effort human label from a GitHub filename. Strips the
 *  extension and replaces dashes/underscores with spaces; falls back to
 *  the raw basename when there's no recognisable shape. */
function labelFromFilename(filename: string): string {
  const base = filename.split(/[\\/]/).pop() ?? filename;
  const dot = base.lastIndexOf(".");
  const stem = dot > 0 ? base.slice(0, dot) : base;
  return stem.replace(/[-_]+/g, " ").trim() || base;
}

/**
 * One-shot dedupe by id. Earlier sync runs (before the merge-by-id
 * fix in `sync()`) wrote both a seed entry (id="ws4-brief",
 * label="Brief", format="PDF", no blob) AND a github-sync entry
 * (id="ws4-brief", label="ws4 brief", color="melon", with blob) into
 * the same workshop bucket. Two chips per deliverable, same id.
 *
 * The fold below runs whenever IDB is read (sanitise) + after the
 * seed migration (`hydrate` re-seed branch). For each id-collision,
 * take the seed entry's PRESENTATION (label, format, color, icon,
 * addedBy) — that's the canonical look — and the github-sync
 * entry's PAYLOAD (blobKey, mimeType, githubPath, githubSha,
 * githubSyncedAt). Result: a single entry per deliverable that
 * reads as the legacy chip AND opens the GitHub-backed file.
 *
 * Idempotent — running it on already-deduped data is a no-op (no
 * collisions → returns the input).
 */
function dedupeById(bucket: Upload[]): Upload[] {
  if (bucket.length === 0) return bucket;
  // Pass 1: index by id, prefer the first occurrence as the canonical
  // "presentation" entry. If a seed appears later than a synced
  // duplicate, swap so the seed wins — its label + format tag is what
  // the user sees in the UI.
  const groups = new Map<string, Upload[]>();
  for (const u of bucket) {
    const existing = groups.get(u.id);
    if (existing) existing.push(u);
    else groups.set(u.id, [u]);
  }
  let changed = false;
  const out: Upload[] = [];
  for (const items of groups.values()) {
    if (items.length === 1) {
      out.push(items[0]!);
      continue;
    }
    changed = true;
    // Find the "presentation" anchor — first seed entry, else first
    // entry with a non-empty `format`, else first entry overall.
    const seedish =
      items.find((u) => u.addedBy === "seed") ??
      items.find((u) => u.format) ??
      items[0]!;
    // Find the "payload" donor — first entry with both `blobKey` and
    // `githubPath`, else first entry with a `blobKey`, else nothing.
    const synced =
      items.find((u) => u.blobKey && u.githubPath) ??
      items.find((u) => u.blobKey);
    const merged: Upload = {
      ...seedish,
      ...(synced
        ? {
            blobKey: synced.blobKey,
            mimeType: synced.mimeType ?? seedish.mimeType,
            githubPath: synced.githubPath ?? seedish.githubPath,
            githubSha: synced.githubSha ?? seedish.githubSha,
            githubSyncedAt: synced.githubSyncedAt ?? seedish.githubSyncedAt,
          }
        : {}),
    };
    out.push(merged);
  }
  return changed ? out : bucket;
}

/** Apply `dedupeById` across every workshop bucket. */
function dedupeAll(entries: EntriesByWorkshop): EntriesByWorkshop {
  let changed = false;
  const out = {} as EntriesByWorkshop;
  for (const id of Object.keys(entries) as Array<Workshop["id"]>) {
    const bucket = entries[id] ?? [];
    const deduped = dedupeById(bucket);
    if (deduped !== bucket) changed = true;
    out[id] = deduped;
  }
  return changed ? out : entries;
}

export const useAssetLocker = create<AssetLockerState>((set, get) => ({
  entriesByWorkshop: emptyEntries(),
  isHydrated: false,
  syncState: isWorkshopBlobSyncEnabled() ? "idle" : "disabled",
  lastSyncedAt: null,
  syncError: null,
  hydrate: async () => {
    if (get().isHydrated) return;
    try {
      const raw = await idbGet(IDB_KEY);
      const persisted = sanitise(raw);
      const markerRaw = await idbGet(SEED_MARKER_KEY);
      const markerVersion =
        typeof markerRaw === "string" ? markerRaw : markerRaw ? "v1" : null;
      // Re-seed when the marker is missing OR the stored seed version
      // is older than the current `SEED_VERSION`. Each workshop's
      // user-added entries (anything where `addedBy !== "seed"`) are
      // preserved; only stale seed entries get refreshed.
      if (markerVersion !== SEED_VERSION) {
        const merged: EntriesByWorkshop = { ...persisted };
        for (const id of Object.keys(SEED_ASSETS) as Array<Workshop["id"]>) {
          const bucket = merged[id] ?? [];
          // Preserve only the entries the user explicitly created
          // (handsave / chat-export / file upload). Drop both the
          // legacy decorative seeds (`addedBy === "seed"`) AND the
          // stale github-sync ghosts (`addedBy === "github-sync"`)
          // that may carry half-written blob refs from earlier CORS
          // / network failures. The post-hydrate sync() pull
          // re-creates them cleanly via the merge-by-id path.
          const userEntries = bucket.filter(
            (u) => u.addedBy !== "seed" && u.addedBy !== "github-sync",
          );
          const freshSeeds = SEED_ASSETS[id].map((u) => ({
            ...u,
            addedAt: Date.now(),
          }));
          merged[id] = [...freshSeeds, ...userEntries];
        }
        // Collapse any id-collisions left over from earlier sync runs
        // BEFORE persisting so the marker move is durable.
        const deduped = dedupeAll(merged);
        set({ entriesByWorkshop: deduped, isHydrated: true });
        void persist(deduped);
        try {
          await idbSet(SEED_MARKER_KEY, SEED_VERSION);
        } catch {
          /* best-effort */
        }
      } else {
        // Same dedupe pass on every boot so a user who already has
        // the v3 marker (and thus skips the re-seed branch) still
        // gets the legacy duplicate chips collapsed on next load.
        const deduped = dedupeAll(persisted);
        set({ entriesByWorkshop: deduped, isHydrated: true });
        if (deduped !== persisted) void persist(deduped);
      }
    } catch {
      set({ isHydrated: true });
    }
    // IDB-served content is now on screen. Kick off a backend
    // reconciliation in the background so the durable view lands shortly
    // after. Fire-and-forget; errors flow through the store's `syncError`
    // field, not via this hydrate promise.
    if (isWorkshopBlobSyncEnabled()) {
      void get().sync();
    }
  },
  add: (workshopId, upload) => {
    const current = get().entriesByWorkshop;
    const list = current[workshopId] ?? [];
    const next: EntriesByWorkshop = {
      ...current,
      [workshopId]: [...list, upload],
    };
    set({ entriesByWorkshop: next });
    void persist(next);

    // RAG sidecar — label/tags searchable; for non-extractable binaries
    // (zip/png/…) this is the only index entry. BFF-only; fire-and-forget.
    upsertUploadIndex(workshopId, upload);

    // Phase 6 — mirror the new entry to GitHub when sync is enabled.
    // Only entries that carry a `blobKey` (real bytes) are durable;
    // seed/placeholder chips stay IDB-only because there's nothing
    // to upload. Fire-and-forget so the UI doesn't block on the
    // round trip; the entry is already in the local store on the
    // synchronous path above.
    if (!upload.blobKey || !isWorkshopBlobSyncEnabled()) return;
    void (async () => {
      try {
        set({ syncState: "syncing", syncError: null });
        const blob = await loadBlob(upload.blobKey!);
        if (!blob) {
          // The blob never landed (saveBlob raced + lost). Skip the
          // upload; the IDB chip stays so the user notices and can
          // re-add.
          set({ syncState: "idle" });
          return;
        }
        const filename = uploadFilename(upload);
        const asset = await uploadWorkshopBlob(
          "uploads",
          workshopId,
          filename,
          blob,
        );
        // Patch the entry with the freshly returned mirror metadata.
        // Read state again — other ops may have mutated the bucket.
        const after = get().entriesByWorkshop;
        const bucket = after[workshopId] ?? [];
        const patched: EntriesByWorkshop = {
          ...after,
          [workshopId]: bucket.map((u) =>
            u.id === upload.id
              ? {
                  ...u,
                  githubPath: asset.path,
                  githubSha: asset.sha,
                  githubSyncedAt: asset.updatedAt,
                }
              : u,
          ),
        };
        set({
          entriesByWorkshop: patched,
          syncState: "synced",
          lastSyncedAt: Date.now(),
          syncError: null,
        });
        void persist(patched);
        // Phase 6 — let sibling tabs know GitHub changed so they can
        // reconcile without waiting for the next polling tick. Guarded
        // on sync-enabled because there's nothing for a peer tab to
        // pull in the IDB-only fallback.
        broadcastSync();
      } catch (err) {
        // GithubStorageError carries a tidy `.message`; anything else
        // we surface verbatim. Leave the local IDB entry alone —
        // the user keeps their copy; the status pill flags the miss.
        const message =
          err instanceof GithubStorageError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Upload failed";
        set({ syncState: "error", syncError: message });
      }
    })();
  },
  update: (workshopId, id, patch) => {
    // IDB-only patch-merge of presentation fields (tags/label/icon/color).
    // Deliberately does NOT touch the GitHub-sync path: a tag edit must not
    // re-upload the blob or churn its SHA. Mirrors the factory's `update`.
    // Label/tag edits still refresh the RAG sidecar when BFF is on.
    const current = get().entriesByWorkshop;
    const list = current[workshopId] ?? [];
    const idx = list.findIndex((u) => u.id === id);
    if (idx < 0) return;
    const updated: Upload = { ...list[idx]!, ...patch };
    const nextList = list.slice();
    nextList[idx] = updated;
    const next: EntriesByWorkshop = { ...current, [workshopId]: nextList };
    set({ entriesByWorkshop: next });
    void persist(next);
    upsertUploadIndex(workshopId, updated);
  },
  remove: (workshopId, id) => {
    const current = get().entriesByWorkshop;
    const list = current[workshopId] ?? [];
    // Capture the doomed entry *before* the filter so we can sweep
    // its blob-store payload (if any). Fire-and-forget: a failed
    // deletion just leaves an orphan blob, which `listBlobKeys`
    // tooling can mop up later.
    const doomed = list.find((u) => u.id === id);
    const next: EntriesByWorkshop = {
      ...current,
      [workshopId]: list.filter((u) => u.id !== id),
    };
    set({ entriesByWorkshop: next });
    void persist(next);
    if (doomed?.blobKey) {
      void deleteBlob(doomed.blobKey);
    }
    void deleteSearchIndex(workshopId, id);
    // Mirror the delete upstream. Needs the path (location); the SHA is a
    // GitHub-only concurrency token (the BFF ignores it), so we no longer
    // require it. Skip when the entry never synced (no path).
    if (isWorkshopBlobSyncEnabled() && doomed?.githubPath) {
      void (async () => {
        try {
          set({ syncState: "syncing", syncError: null });
          await deleteWorkshopBlob(doomed.githubPath!, doomed.githubSha);
          set({
            syncState: "synced",
            lastSyncedAt: Date.now(),
            syncError: null,
          });
          // Phase 6 — see the `add()` broadcast for rationale; the
          // upstream delete is just another GitHub mutation that peer
          // tabs need to learn about quickly.
          broadcastSync();
        } catch (err) {
          const message =
            err instanceof GithubStorageError
              ? err.message
              : err instanceof Error
                ? err.message
                : "Delete failed";
          set({ syncState: "error", syncError: message });
        }
      })();
    }
  },
  sync: async () => {
    // No-op when no blob backend is configured. Keeps the IDB-only dev
    // path free of accidental adapter calls.
    if (!isWorkshopBlobSyncEnabled()) {
      set({ syncState: "disabled" });
      return;
    }
    // Soft-skip when OIDC isn't ready yet. App boot hydrates (and the
    // login page used to poll) before a bearer token exists; treating
    // that as a hard error paints "Sync error" / "Not signed in" on the
    // Asset Locker until the next 15s tick. Stay idle and let
    // `useGithubSync` retry once the session lands.
    if (!(await isDocsAuthReady())) {
      set({ syncState: "idle", syncError: null });
      return;
    }
    set({ syncState: "syncing", syncError: null });
    try {
      // Walk every workshop bucket. The reconciliation per bucket is
      // independent — we accumulate the merged state, then commit a
      // single `set` + `persist` at the end so the UI rerenders once.
      const startState = get().entriesByWorkshop;
      const nextState: EntriesByWorkshop = { ...startState };

      for (const workshopId of WORKSHOP_IDS) {
        const remote = await listWorkshopBlobs("uploads", workshopId);
        const localBucket = nextState[workshopId] ?? [];

        // GitHub-by-path index for the deleted-upstream check below.
        const remoteByPath = new Map(remote.map((a) => [a.path, a]));
        // IDB-by-path index — only entries that already have a
        // `githubPath` (i.e. previously synced).
        const localByGithubPath = new Map<string, Upload>();
        for (const u of localBucket) {
          if (u.githubPath) localByGithubPath.set(u.githubPath, u);
        }

        // IDB-by-id index — used to merge a remote asset into an
        // existing seed entry whose id matches the filename stem. This
        // keeps the original `label` + `format` tag (e.g. "Brief
        // [PDF]") rather than dropping a duplicate "ws4-brief.pdf"
        // chip alongside the seed entry. User uploads mirror under a
        // label-led filename (`uploadFilename`), so each entry is also
        // indexed by that derived stem — a pull racing an unfinished
        // mirror then patches the existing chip instead of cloning it.
        const localById = new Map(localBucket.map((u) => [u.id, u]));
        for (const u of localBucket) {
          const stem = uploadFilename(u).replace(/\.[^.]+$/, "");
          if (!localById.has(stem)) localById.set(stem, u);
        }

        // ── Step 1 — pull. For each remote asset:
        //   (a) if its filename stem matches an existing seed/local
        //       entry's id → PATCH that entry in place with the blob
        //       backing + GitHub mirror metadata. The original label
        //       + format + icon + color survive verbatim.
        //   (b) if no local entry matches → mint a fresh Upload using
        //       the filename for the label (legacy fallback for
        //       blobs uploaded outside the app). ──
        const pulled: Upload[] = [];
        const patches = new Map<string, Upload>();
        for (const asset of remote) {
          if (localByGithubPath.has(asset.path)) continue;
          // Download the bytes, write the blob to local cache, then
          // wrap it with the inferred MIME so `<Blob.type>` matches
          // what the lightbox expects.
          const rawBlob = await downloadWorkshopBlob(asset.path);
          if (!rawBlob) continue; // 404 race — treat as a soft miss.
          const filename = asset.path.split("/").pop() ?? asset.path;
          const ext = extFromFilename(filename);
          const mimeType = mimeFromExt(ext);
          const typed = new Blob([rawBlob], { type: mimeType });
          // Blob keys mirror the existing snapshot convention but with
          // a `gh:` prefix so they're easy to spot in dev tooling.
          const blobKey = `gh:${workshopId}:${filename}`;
          await saveBlob(blobKey, typed);
          // Derive an id that's stable across pulls — the filename
          // stem usually IS the original id (we mint `<id>.<ext>`),
          // but for assets uploaded outside the app the stem reads
          // human-enough to use as a fallback id.
          const stem = filename.replace(/\.[^.]+$/, "");
          const remoteUpdated = Date.parse(asset.updatedAt);
          const addedAtMs = Number.isFinite(remoteUpdated)
            ? remoteUpdated
            : Date.now();

          const existing = localById.get(stem);
          if (existing) {
            // Patch path — preserve every presentation field. Only
            // the blob backing + GitHub mirror metadata changes. We
            // leave `addedBy` alone too: if it was "seed" before, it
            // stays "seed" so a future re-seed migration can find it.
            patches.set(stem, {
              ...existing,
              blobKey,
              mimeType,
              githubPath: asset.path,
              githubSha: asset.sha,
              githubSyncedAt: asset.updatedAt,
            });
          } else {
            // Mint path — pristine new entry for an unknown asset.
            pulled.push({
              id: stem,
              label: labelFromFilename(filename),
              icon: "",
              color: "melon",
              blobKey,
              mimeType,
              addedAt: addedAtMs,
              addedBy: "github-sync",
              githubPath: asset.path,
              githubSha: asset.sha,
              githubSyncedAt: asset.updatedAt,
            });
          }
        }

        // ── Step 2 — push. Anything local with bytes (`blobKey`) but
        //    no `githubPath` gets uploaded; we patch the in-memory
        //    bucket with the returned mirror metadata. Start from the
        //    Step-1 patched view of the bucket so a freshly-merged
        //    blob doesn't get re-uploaded as if it were local-only. ──
        const patchedBucket = localBucket.map((u) => patches.get(u.id) ?? u);
        const pushed = await Promise.all(
          patchedBucket.map(async (u) => {
            if (!u.blobKey || u.githubPath) return u;
            const blob = await loadBlob(u.blobKey);
            if (!blob) return u;
            try {
              const filename = uploadFilename(u);
              const asset = await uploadWorkshopBlob(
                "uploads",
                workshopId,
                filename,
                blob,
              );
              return {
                ...u,
                githubPath: asset.path,
                githubSha: asset.sha,
                githubSyncedAt: asset.updatedAt,
              };
            } catch (err) {
              // Per-entry failures don't abort the whole sync; the
              // outer try/catch only handles adapter-level fatals.
              // A known adapter error (GitHub 409, BFF 4xx) means leave
              // the local entry untouched and let the next reconciliation
              // tick pick it up.
              if (err instanceof GithubStorageError || err instanceof DocsApiError) {
                return u;
              }
              throw err;
            }
          }),
        );

        // ── Step 3 — upstream deletions. Entries with a `githubPath`
        //    that isn't in the GitHub list mean the asset was deleted
        //    elsewhere. We deliberately do NOT cascade the delete
        //    locally: a follow-up pass can render a "stale" badge on
        //    these chips when the UI is ready. Surfacing the divergence
        //    > silently dropping the user's local copy. ──
        for (const u of pushed) {
          if (u.githubPath && !remoteByPath.has(u.githubPath)) {
            // Marker only — no state change yet. Comment is the spec.
            void u;
          }
        }

        nextState[workshopId] = [...pushed, ...pulled];
      }

      // Defence in depth: even with the merge-by-id step above, a
      // race (e.g. user-saved entry committed concurrently with a
      // pull) could leave a stray duplicate. Collapse before commit.
      const finalState = dedupeAll(nextState);

      set({
        entriesByWorkshop: finalState,
        syncState: "synced",
        lastSyncedAt: Date.now(),
        syncError: null,
      });
      void persist(finalState);
      // Phase 6 — broadcast after a full reconciliation so a sibling
      // tab kicked off by something else (a polling tick, a manual
      // pill click) propagates promptly. The 10 s `staleTime` on the
      // polling hook dedupes if multiple tabs race.
      broadcastSync();
    } catch (err) {
      // Known adapter errors (`GithubStorageError` / `DocsApiError`) are
      // folded into the store status — other throws bubble so they show up
      // in console / error boundary instead of being silently swallowed.
      if (err instanceof GithubStorageError || err instanceof DocsApiError) {
        set({ syncState: "error", syncError: err.message });
        return;
      }
      set({ syncState: "error", syncError: "Unexpected sync failure" });
      throw err;
    }
  },
}));
